import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import Nav from './Nav';
import Footer from './Footer';
import Sidebar from './Sidebar';

const DOCS_BASE = 'https://docs.metha.life/';

export type PageProps = {
  /** ctx pill in the brand line — "auction", "auditing", "request #19", … */
  ctxLabel?: string;
  /** Persona accent: lab=blue, auditor=purple, funder=amber, default=green */
  ctx?: 'lab' | 'auditor' | 'funder';
  /** Document title */
  title?: string;
  /** Slug appended to docs.metha.life/ for the floating help button.
   *  Set to null to hide the button on a page (default: shown → overview). */
  docsPath?: string | null;
  /** Optional human-readable label for the help-button tooltip. */
  docsLabel?: string;
  children: ReactNode;
  footerLeft?: ReactNode;
  footerRight?: ReactNode;
};

/**
 * Standard shell: sidebar (icon rail) + top bar + main + footer +
 * body[data-ctx] persona accent. Pages compose their own content as
 * children. The Sidebar owns mobile-drawer state; the topbar's hamburger
 * just opens it via the lifted setter below.
 */
export default function Page({
  ctxLabel,
  ctx,
  title,
  docsPath = 'overview',
  docsLabel,
  children,
  footerLeft,
  footerRight,
}: PageProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (ctx) document.body.setAttribute('data-ctx', ctx);
    else document.body.removeAttribute('data-ctx');
    return () => document.body.removeAttribute('data-ctx');
  }, [ctx]);

  useEffect(() => {
    if (title) document.title = title;
  }, [title]);

  const docsHref = docsPath ? DOCS_BASE + docsPath.replace(/^\/+/, '') : null;

  return (
    <div className="app">
      <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
      <div className="app-main">
        <Nav ctxLabel={ctxLabel} onOpenMobileMenu={() => setMobileOpen(true)} />
        <main className="shell">{children}</main>
        <Footer left={footerLeft} right={footerRight} />
      </div>
      {docsHref && (
        <a
          className="docs-fab"
          href={docsHref}
          target="_blank"
          rel="noopener noreferrer"
          title={docsLabel ? `Help: ${docsLabel}` : 'Help — open Metha docs in a new tab'}
          aria-label={docsLabel ? `Help: ${docsLabel}` : 'Open Metha docs'}
        >
          ?
        </a>
      )}
    </div>
  );
}
